import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Sparkles } from 'lucide-react';
import Hero from '@/components/Hero';
import SuperSlider from '@/components/SuperSlider';
import ArtworkCard from '@/components/ArtworkCard';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t, localize } = useTranslation();

  const settings = useAdminStore((s) => s.settings);
  const initialized = useAdminStore((s) => s.initialized);
  const aboutSection = useAdminStore((s) => s.settings?.homeAboutSection);
  const allArtworks = useAdminStore((s) => s.artworks);
  const explicitFeatured = allArtworks.filter((a) => a.isFeatured);
  // Fallback: if no artworks are explicitly featured, show the latest artworks with images
  const featured = explicitFeatured.length > 0
    ? explicitFeatured
    : allArtworks.filter((a) => a.images && a.images.length > 0).slice(0, 5);
  const shopPreview = allArtworks.filter((a) => a.isForSale).slice(0, 6);

  return (
    <>
      <Hero />

      {/* About Section */}
      <section className="px-4 sm:px-6 md:px-24 py-16 sm:py-24 md:py-40 bg-white dark:bg-black transition-colors duration-700">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-10 sm:gap-16 md:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="flex-1 space-y-5 sm:space-y-8"
          >
            {localize(aboutSection?.label) && (
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.4em] sm:tracking-[0.6em] text-black/20 dark:text-white/20 uppercase">
                {localize(aboutSection?.label)}
              </span>
            )}
            <h2 className="text-3xl sm:text-5xl md:text-7xl font-syncopate font-bold leading-none tracking-tighter uppercase text-black dark:text-white">
              {(localize(aboutSection?.title) || 'FINE ART\nPHOTOGRAPHY').split('\n').map((line, i) => (
                <React.Fragment key={i}>{i > 0 && <br />} {line}</React.Fragment>
              ))}
            </h2>
            <p className="text-black/50 dark:text-white/50 text-base sm:text-lg md:text-xl font-light leading-relaxed max-w-md">
              {localize(aboutSection?.description) || "Gallery Fotoatelier is Tbilisi's premier fine art photography gallery, dedicated to showcasing exceptional contemporary works from Georgian and international artists."}
            </p>
            <motion.button
              onClick={() => navigate('/about')}
              whileHover={{ x: 6 }}
              className="group flex items-center gap-3 pt-2"
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/40 dark:text-white/40 group-hover:text-black dark:group-hover:text-white transition-colors">
                {t.nav.home === 'Home' ? 'See More' : 'მეტის ნახვა'}
              </span>
              <span className="w-8 h-px bg-black/20 dark:bg-white/20 group-hover:w-12 group-hover:bg-black dark:group-hover:bg-white transition-all duration-300" />
            </motion.button>
          </motion.div>
          {aboutSection?.image ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="flex-1 w-full aspect-square max-w-[280px] sm:max-w-none rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl shadow-black/5 dark:shadow-white/5 border border-black/5 dark:border-white/5 mx-auto md:mx-0"
            >
              {/* fetchpriority lowercase: this React version passes the camelCase form
                  straight to the DOM and logs an unknown-prop warning. */}
              <img src={aboutSection.image} alt={localize(aboutSection.title) || 'About'} className="w-full h-full object-cover" loading="eager" {...{ fetchpriority: 'high' }} />
            </motion.div>
          ) : initialized ? (
            <div className="flex-1 w-full aspect-square max-w-[280px] sm:max-w-none rounded-2xl sm:rounded-3xl overflow-hidden border border-black/5 dark:border-white/5 bg-[#f9f9f9] dark:bg-[#0a0a0a] p-6 sm:p-12 relative mx-auto md:mx-0">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                className="w-full h-full border border-black/5 dark:border-white/5 rounded-full flex items-center justify-center"
              >
                <div className="w-1/2 h-1/2 border border-black/10 dark:border-white/10 rounded-full" />
              </motion.div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles size={60} className="text-black/5 dark:text-white/5" />
              </div>
            </div>
          ) : (
            <div className="flex-1 w-full aspect-square max-w-[280px] sm:max-w-none rounded-2xl sm:rounded-3xl bg-[#fafafa] dark:bg-[#0a0a0a] mx-auto md:mx-0" />
          )}
        </div>
      </section>

      {/* Featured Slider */}
      <SuperSlider artworks={featured} />

      {/* Shop Preview */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 bg-white dark:bg-black transition-colors duration-700">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-10 sm:mb-16 gap-6 sm:gap-10">
            <div className="max-w-xl">
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.4em] sm:tracking-[0.5em] text-black/20 dark:text-white/20 uppercase mb-3 sm:mb-4 block">
                {t.shop.title}
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-6xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none transition-colors duration-700">
                {localize(settings?.homeCollectionTitle) || (t.nav.home === 'Home' ? 'COLLECTION' : 'კოლექცია')}
              </h2>
            </div>
            <button
              onClick={() => navigate('/shop')}
              className="group flex items-center gap-3 px-8 py-4 border border-black/5 dark:border-white/5 text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all text-[10px] font-bold uppercase tracking-widest rounded-full shadow-sm"
            >
              {t.shop.title} <ShoppingBag size={14} />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-6 md:gap-8">
            {shopPreview.map((artwork) => (
              <ArtworkCard key={artwork._id} artwork={artwork} displayOnly />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
